import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { v4 as uuid } from 'uuid';
import classNames from 'classnames';
import { SIZES } from './constants';
import Icon from '../icon';
import useInteractiveAttributes from '../../hooks/use-interactive-handler';
import IndeterminateTile from './components/indeterminate-tile';
import AaSymbol from './components/aa-symbol';
import Tooltip from '../tooltip';

function ColorTile({
  size = SIZES.extraLarge,
  active = false,
  backgroundColor = '#fff',
  indeterminate = false,
  tooltipText = null,
  onConfigureClick = null,
  onClick,
  fullwidth = false,
  empty = false,
}) {
  const tooltipId = useRef(`color-tile-${uuid()}`);

  const isMobile = bowser.mobile;
  const isTablet = bowser.tablet;
  const isTouchDevice = isMobile || isTablet;
  const isUnspecified =
    !indeterminate && !(backgroundColor && CSS.supports('background-color', backgroundColor));

  const classes = classNames('ws-ui-color-tile', `ws-ui-color-tile--${size}`, {
    'ws-ui-color-tile--active': active,
    'ws-ui-color-tile--indeterminate': indeterminate,
    'ws-ui-color-tile--unspecified': isUnspecified,
    'ws-ui-color-tile--fullwidth': fullwidth,
  });

  const onFloatingButtonClick = useCallback(
    (e) => {
      e.stopPropagation();
      onConfigureClick();
    },
    [onConfigureClick]
  );

  const interactiveAttributes = useInteractiveAttributes(onClick);

  if (indeterminate) {
    return (
      <IndeterminateTile
        className={classes}
        isTouchDevice={isTouchDevice}
        onClick={onClick}
        tooltipText={tooltipText}
      />
    );
  }

  if (isUnspecified) {
    return <div className={classes} onClick={onClick} {...interactiveAttributes} />;
  }

  return (
    <div
      className={classes}
      onClick={onClick}
      style={{ backgroundColor }}
      {...interactiveAttributes}
      {...(tooltipText && {
        'data-tip': true,
        'data-for': tooltipId.current,
      })}
    >
      {!empty && (
        <div className="ws-ui-color-tile__symbol">
          <AaSymbol backgroundColor={backgroundColor} />
        </div>
      )}
      {!isTouchDevice && onConfigureClick && (
        <div className="ws-ui-color-tile__floating-icon-button" onClick={onFloatingButtonClick}>
          <Icon glyph="slider-settings-vertical" size="12" withScaleFactor />
        </div>
      )}
      {!isTouchDevice && tooltipText && <Tooltip id={tooltipId.current} text={tooltipText} />}
    </div>
  );
}

ColorTile.propTypes = {
  onClick: PropTypes.func.isRequired,
  size: PropTypes.oneOf(Object.values(SIZES)),
  backgroundColor: PropTypes.string,
  active: PropTypes.bool,
  fullwidth: PropTypes.bool,
  empty: PropTypes.bool,
  indeterminate: PropTypes.bool,
  onConfigureClick: PropTypes.func,
  tooltipText: PropTypes.string,
};

export default ColorTile;
